import { Box, Dialog, IconButton, Typography, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import { HEADING_FONT } from "../theme/fonts";
import { COLORS } from "../theme/tokens";
import ServerForm from "./ServerForm";

// Popup modal for adding / editing a server (spec §5.3). The flat white dialog
// shell has a blue gradient header (the spec reserves gradients for modal
// headers) and renders the shared ServerForm body below.
//
// Props:
//   open      — controls visibility.
//   onClose   — close without saving (backdrop / close button / Cancel).
//   onSuccess — called after a successful save (receives { isEdit }).
//   editId    — null = add; an id = edit.
function AddServerModal({ open, onClose, onSuccess, editId = null }) {
  const isEdit = editId != null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: "16px",
            background: "#fff",
            border: `1px solid ${COLORS.hairline}`,
            boxShadow: "0 24px 64px rgba(16, 24, 40, 0.18)",
            backgroundImage: "none",
            overflow: "hidden",
          },
        },
      }}
    >
      {/* Gradient header */}
      <Box
        sx={{
          position: "relative",
          background: `linear-gradient(135deg, ${COLORS.primaryDark} 0%, ${COLORS.primary} 60%, #60A5FA 100%)`,
          color: "#fff",
          px: { xs: 3, md: 4 },
          py: 3,
        }}
      >
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            color: "rgba(255,255,255,0.9)",
            "&:hover": { background: "rgba(255,255,255,0.18)", color: "#fff" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "12px",
              background: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <DnsOutlinedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
                opacity: 0.85,
              }}
            >
              {isEdit ? "Edit Server" : "Add Server"}
            </Typography>
            <Typography
              sx={{
                fontFamily: HEADING_FONT,
                fontSize: { xs: 20, md: 24 },
                fontWeight: 700,
                letterSpacing: "-0.5px",
                lineHeight: 1.15,
              }}
            >
              {isEdit ? "Update server details" : "Register a new server"}
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Form body — scrolls if taller than the viewport */}
      <Box
        sx={{
          p: { xs: 2.5, md: 3 },
          maxHeight: { xs: "70vh", md: "72vh" },
          overflowY: "auto",
          background: COLORS.canvas,
        }}
      >
        {/* Remount the form per target so fields reset/reload cleanly between
            Add and different Edit targets. */}
        <ServerForm
          key={editId ?? "new"}
          serverId={editId}
          onSuccess={onSuccess}
          onCancel={onClose}
        />
      </Box>
    </Dialog>
  );
}

export default AddServerModal;
